import { QUARTERS_PER_HOUR } from '../shared/hours';
import { normalizeName } from '../shared/naming';
import { roundQuantity } from '../schedule/unit-of-measure';
import { PRICE_DECIMALS, TOTAL_DECIMALS } from './material-price';

/**
 * O custo da hora de mão de obra no orçamento (Fase 29, etapa 3 — RN-399).
 *
 * A mão de obra sai da consolidação em homem-hora, por **função** (RN-166, RN-252). O orçamento
 * põe R$ nessa hora, e o R$ vem da folha — o mesmo custo que o painel usa para o realizado, para
 * que o orçado e o realizado custem igual pela mesma hora (D11, D12):
 *
 * ```text
 * R$/h da função = média simples do custo do mês dos ativos com salário ÷ horas úteis do mês
 * custo do mês   = salário × (1 + encargos)
 * ```
 *
 * A cascata, da fonte mais próxima para a mais distante:
 *
 * 1. **a folha da obra** — os ativos com salário da função nesta obra;
 * 2. **a média do sistema** — a obra que ainda não contratou a função usa o que ela custa nas
 *    outras;
 * 3. **o provisório** — a função que ninguém tem ainda, com o número que alguém digitou.
 *
 * As horas úteis são as do mês corrente, pelo calendário da obra, como na tela de mão de obra
 * (RN-059): o mês com feriado custa mais por hora, e o orçamento diz de que mês é a conta.
 */

export type LaborRateSource = 'OBRA' | 'SISTEMA' | 'PROVISORIO';

/**
 * Por que não há R$/h, ou por que ele não saiu da folha da obra:
 *
 * - `SEM_CUSTO`: ninguém da função tem salário, na obra nem no sistema, e não há provisório;
 * - `SEM_HORAS`: o calendário não tem hora útil no mês — dividir daria infinito;
 * - `SEM_FUNCAO`: o item de mão de obra não aponta uma função (RN-252), e sem função não há folha;
 * - `NAO_E_HORA`: o item não é medido em hora — R$/h vezes "dia" erraria calado.
 */
export type LaborRateWarning = 'SEM_CUSTO' | 'SEM_HORAS' | 'SEM_FUNCAO' | 'NAO_E_HORA';

export const LABOR_SOURCE_LABEL: Readonly<Record<LaborRateSource, string>> = {
  OBRA: 'Folha da obra',
  SISTEMA: 'Média do sistema',
  PROVISORIO: 'Provisório',
};

export const LABOR_WARNING_LABEL: Readonly<Record<LaborRateWarning, string>> = {
  SEM_CUSTO: 'sem salário na folha — informe o provisório',
  SEM_HORAS: 'o calendário não tem hora útil no mês',
  SEM_FUNCAO: 'o item não aponta uma função',
  NAO_E_HORA: 'o item não é medido em hora',
};

/** O que entra na conta de uma função. */
export interface FunctionCostInput {
  /** O custo do mês com encargos, em centavos, de cada ativo com salário **da obra**. */
  readonly projectMonthlyCosts: readonly number[];
  /** O mesmo, de todas as obras do sistema (a obra inclusive). */
  readonly systemMonthlyCosts: readonly number[];
  /** O R$/h que alguém digitou para a função nesta obra. */
  readonly provisionalPerHour?: number;
}

export interface ResolvedLaborRate {
  /** Em reais por hora, com quatro casas. Ausente = sem custo. */
  readonly perHour?: number;
  readonly source?: LaborRateSource;
  /** Quantos funcionários entraram na média — "3 na obra", "12 no sistema". */
  readonly employees: number;
  readonly warning?: LaborRateWarning;
}

const media = (valores: readonly number[]): number =>
  valores.reduce((soma, valor) => soma + valor, 0) / valores.length;

/** O R$/h da função pela cascata obra → sistema → provisório (D11). */
export function resolveFunctionRate(
  input: FunctionCostInput,
  monthlyQuarterHours: number,
): ResolvedLaborRate {
  const horas = monthlyQuarterHours / QUARTERS_PER_HOUR;
  const daFolha = (custos: readonly number[], source: LaborRateSource): ResolvedLaborRate => ({
    perHour: roundQuantity(media(custos) / 100 / horas, PRICE_DECIMALS),
    source,
    employees: custos.length,
  });

  if (horas > 0 && input.projectMonthlyCosts.length > 0) {
    return daFolha(input.projectMonthlyCosts, 'OBRA');
  }
  if (horas > 0 && input.systemMonthlyCosts.length > 0) {
    return daFolha(input.systemMonthlyCosts, 'SISTEMA');
  }
  const motivo: LaborRateWarning | undefined =
    horas > 0 ? undefined : input.systemMonthlyCosts.length > 0 ? 'SEM_HORAS' : undefined;
  if (input.provisionalPerHour !== undefined) {
    return {
      perHour: input.provisionalPerHour,
      source: 'PROVISORIO',
      employees: 0,
      ...(motivo === undefined ? {} : { warning: motivo }),
    };
  }
  return { employees: 0, warning: motivo ?? 'SEM_CUSTO' };
}

/** A chave da consolidação de um item de mão de obra com função — a mesma de `consolidationKey`. */
export function laborFunctionKey(jobRoleId: string): string {
  return `funcao:${jobRoleId}`;
}

/** A chave é de mão de obra? Com função, ou pela descrição do item que ainda não tem uma. */
export function isLaborRateKey(key: string): boolean {
  return key.startsWith('funcao:') || key.startsWith('descricao:');
}

/**
 * A unidade do item é hora?
 *
 * A mão de obra sai em homem-hora, e é por isso que o R$/h a multiplica. A unidade ausente é
 * hora — é como a consolidação a trata (RN-166); "DIA" ou "MÊS" não são, e multiplicar por R$/h
 * daria um número sem sentido que ninguém estranharia.
 */
export function isHourUnit(unit: string | undefined): boolean {
  if (unit === undefined || unit.trim() === '') return true;
  const texto = normalizeName(unit).replace(/[^a-z]/g, '');
  return ['h', 'hh', 'hr', 'hrs', 'hora', 'horas', 'homemhora', 'horahomem'].includes(texto);
}

/** O que a obra sabe de cada função, e os provisórios por chave. */
export interface LaborRates {
  /** Por chave `funcao:<id>`. */
  readonly functions: ReadonlyMap<string, ResolvedLaborRate>;
  /** O provisório por chave — de função ou de descrição. */
  readonly provisionals: ReadonlyMap<string, number>;
}

/**
 * O R$/h de um item de mão de obra da consolidação.
 *
 * O item com função usa a cascata da função. O item sem função não tem folha para consultar
 * (RN-252): vale o provisório da descrição, e sem ele o aviso pede a função.
 */
export function resolveLaborItemRate(
  item: { readonly key: string; readonly unit?: string },
  rates: LaborRates,
): ResolvedLaborRate {
  if (!isHourUnit(item.unit)) return { employees: 0, warning: 'NAO_E_HORA' };
  if (item.key.startsWith('funcao:')) {
    const daFuncao = rates.functions.get(item.key);
    if (daFuncao !== undefined) return daFuncao;
    const provisorio = rates.provisionals.get(item.key);
    return provisorio === undefined
      ? { employees: 0, warning: 'SEM_CUSTO' }
      : { perHour: provisorio, source: 'PROVISORIO', employees: 0 };
  }
  const provisorio = rates.provisionals.get(item.key);
  return provisorio === undefined
    ? { employees: 0, warning: 'SEM_FUNCAO' }
    : { perHour: provisorio, source: 'PROVISORIO', employees: 0 };
}

export interface PricedLabor<T> {
  readonly item: T;
  readonly rate: ResolvedLaborRate;
  /** Horas × R$/h, em centavos. Ausente = sem custo, e fora do total. */
  readonly total?: number;
}

export interface LaborBudgetSummary {
  readonly total: number;
  readonly hours: number;
  readonly withoutRate: number;
}

/** Põe R$ na mão de obra consolidada, linha a linha, e soma (D15). */
export function priceLabor<
  T extends { readonly key: string; readonly unit?: string; readonly total: number },
>(
  items: readonly T[],
  rates: LaborRates,
): { readonly rows: readonly PricedLabor<T>[]; readonly summary: LaborBudgetSummary } {
  let total = 0;
  let hours = 0;
  let withoutRate = 0;
  const rows = items.map((item): PricedLabor<T> => {
    const rate = resolveLaborItemRate(item, rates);
    if (rate.perHour === undefined) {
      withoutRate += 1;
      return { item, rate };
    }
    const linha = roundQuantity(item.total * rate.perHour, TOTAL_DECIMALS);
    total += linha;
    hours += item.total;
    return { item, rate, total: linha };
  });
  return {
    rows,
    summary: { total: roundQuantity(total, TOTAL_DECIMALS), hours, withoutRate },
  };
}
